import type { BibleLocation } from '@skjv/common';
import { parseVerseId } from './parse-verse-location';

/** Setup click delegation for Strong's words, verse numbers, and pericope headings */
export function setupClickHandler(
  container: HTMLElement,
  callbacks: {
    onStrongsClick?: (strongsNumbers: string, location: BibleLocation, element: HTMLElement, englishWord?: string) => void;
    onVerseClick?: (location: BibleLocation) => void;
    onPericopeClick?: (location: BibleLocation, title: string, anchorRect: DOMRect) => void;
  },
) {
  container.addEventListener('click', (e) => {
    const target = e.target as HTMLElement;

    if (target.classList.contains('strongs-word')) {
      e.preventDefault();
      const strongsNumbers = target.dataset.strongs;
      const locationStr = target.dataset.location;
      if (strongsNumbers && locationStr && callbacks.onStrongsClick) {
        const parts = locationStr.split('-').map(Number);
        const location: BibleLocation = {
          testamentIndex: parts[0],
          bookIndex: parts[1],
          chapterIndex: parts[2],
          verseIndex: parts[3],
        };
        const englishWord = target.textContent?.trim();
        callbacks.onStrongsClick(strongsNumbers, location, target, englishWord);
      }
    }

    if (target.classList.contains('verse-num')) {
      e.preventDefault();
      const verseDiv = target.closest('.verse');
      if (verseDiv && callbacks.onVerseClick) {
        const location = parseVerseId(verseDiv.id);
        callbacks.onVerseClick(location);
      }
    }

    if (target.classList.contains('pericope-heading')) {
      e.preventDefault();
      const location = parsePericopeHeadingLocation(target);
      const title = target.textContent?.trim();
      if (location && title && callbacks.onPericopeClick) {
        callbacks.onPericopeClick(location, title, target.getBoundingClientRect());
      }
    }
  });
}

/** Resolve a pericope heading's location from its data attribute, falling
 *  back to the next .verse sibling for stale cached HTML without it. */
function parsePericopeHeadingLocation(heading: HTMLElement): BibleLocation | null {
  const locationStr = heading.dataset.location;
  if (locationStr) {
    const parts = locationStr.split('-').map(Number);
    if (parts.length === 4 && parts.every((n) => Number.isFinite(n))) {
      return {
        testamentIndex: parts[0],
        bookIndex: parts[1],
        chapterIndex: parts[2],
        verseIndex: parts[3],
      };
    }
  }
  let sibling = heading.nextElementSibling;
  while (sibling && !sibling.classList.contains('verse')) {
    sibling = sibling.nextElementSibling;
  }
  return sibling ? parseVerseId(sibling.id) : null;
}

/** Setup drag handling for verse numbers */
export function setupDragHandler(container: HTMLElement) {
  container.addEventListener('dragstart', (e) => {
    const target = e.target as HTMLElement;
    if (target.classList.contains('verse-num')) {
      const verseDiv = target.closest('.verse');
      if (verseDiv) {
        const location = parseVerseId(verseDiv.id);
        e.dataTransfer?.setData('application/json', JSON.stringify(location));
        e.dataTransfer!.effectAllowed = 'copy';
      }
    }
  });
}
